import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './style/print.css';
import RecipeEditorModal from './RecipeEditorModal';
import DosingCalculatorModal from './DosingCalculatorModal';
import ThemePicker from './components/ThemePicker';
import PantryMatcher from './components/PantryMatcher';
import OnboardingOverlay from './components/OnboardingOverlay';
import { supabase } from './utils/supabaseClient';
import { checkPremiumStatus } from './utils/premiumUtils';
import { useToast } from './components/Toast';

// ─── Mood quick-filter config (maps to actual effects in the recipe data) ───
const MOODS = [
  { label: 'Wind Down',     effects: ['Comforting', 'Balanced'] },
  { label: 'Get Creative',  effects: ['Elevating', 'Energizing'] },
  { label: 'Social Vibe',   effects: ['Social', 'Invigorating'] },
  { label: 'Treat Yourself',effects: ['Indulgent', 'Refreshing'] },
  { label: 'Anything Goes', effects: [] }, // empty = no filter
];

// ─── Meal type chips (map to recipe tags) ────────────────────────────────────
const MEAL_TYPES = [
  { label: 'Breakfast', tags: ['breakfast'] },
  { label: 'Lunch',     tags: ['lunch', 'midday'] },
  { label: 'Dinner',    tags: ['dinner'] },
  { label: 'Dessert',   tags: ['dessert', 'sweet', 'treat'] },
  { label: 'Drinks',    tags: ['beverage', 'drink', 'cocktail', 'mocktail'] },
];

// ─── Recipe categories (map directly to recipe.category field) ───────────────
const CATEGORIES = [
  'Rise & Shine',
  'Starters & Snacks',
  'Soups & Salads',
  'Mains',
  'Sweet Endings',
  'Sips & Elixirs',
  'Pantry & Infusions',
];

// ─── Intensity based on mg/serving ────────────────────────────────────────────
function getIntensity(mg) {
  if (mg <= 5)  return { label: 'Mild',     dots: 1, color: '#16a34a' };
  if (mg <= 15) return { label: 'Moderate', dots: 2, color: '#d97706' };
  return          { label: 'Strong',   dots: 3, color: '#dc2626' };
}

function IntensityDots({ mg }) {
  const { label, dots, color } = getIntensity(mg);
  return (
    <span className="flex items-center gap-1" title={`${label} · ~${mg}mg/serving`}>
      {[1, 2, 3].map(i => (
        <span
          key={i}
          className="inline-block w-2 h-2 rounded-full"
          style={{ backgroundColor: i <= dots ? color : '#d1d5db' }}
        />
      ))}
      <span className="text-xs ml-1 font-medium" style={{ color }}>{label}</span>
    </span>
  );
}

// ─── Common cooking fractions to snap to when scaling ────────────────────────
const COOK_FRACTIONS = [
  [1, 8], [1, 4], [1, 3], [3, 8], [1, 2],
  [5, 8], [2, 3], [3, 4], [7, 8],
];

function toFraction(val) {
  if (val <= 0) return String(Math.round(val));
  if (Number.isInteger(val)) return String(val);

  const whole = Math.floor(val);
  const frac  = val - whole;

  // Snap to nearest common cooking fraction if within 2%
  let best = null, bestDiff = Infinity;
  for (const [n, d] of COOK_FRACTIONS) {
    const diff = Math.abs(frac - n / d);
    if (diff < bestDiff) { bestDiff = diff; best = [n, d]; }
  }

  if (best && bestDiff < 0.021) {
    const [n, d] = best;
    if (n >= d) return String(whole + 1);        // snapped to whole
    if (whole === 0) return `${n}/${d}`;          // e.g. "3/4"
    return `${whole} ${n}/${d}`;                  // e.g. "1 1/4"
  }

  // Fall back to decimal, trimming trailing zero
  return val.toFixed(1).replace(/\.0$/, '');
}

// ─── Scale a single ingredient string (handles mixed numbers + fractions) ────
function scaleIngredient(str, scale) {
  if (scale === 1) return str;
  // Match: mixed number "1 1/2", pure fraction "3/4", or plain number "2" / "2.5"
  return str.replace(
    /^(\d+)\s+(\d+)\/(\d+)|^(\d+)\/(\d+)|^(\d+\.?\d*)/,
    (_, w, fn, fd, n, d, plain) => {
      let val;
      if (w !== undefined)      val = parseInt(w, 10) + parseInt(fn, 10) / parseInt(fd, 10);
      else if (n !== undefined) val = parseInt(n, 10) / parseInt(d, 10);
      else                      val = parseFloat(plain);
      return toFraction(val * scale);
    }
  );
}

// ─── Deterministic "Tonight's Pick" based on today's date ────────────────────
function getTodaysPick(recipes) {
  if (!recipes.length) return null;
  const seed = new Date().toDateString();
  const idx = seed.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % recipes.length;
  return recipes[idx];
}

export default function ChefBuddeeUI() {
  const showToast = useToast();

  // ── Core state ────────────────────────────────────────────────────────────
  const [recipes, setRecipes] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedStrain, setSelectedStrain] = useState('');
  const [selectedEffect, setSelectedEffect] = useState('');
  const [selectedTerpene, setSelectedTerpene] = useState('');
  const [userType, setUserType] = useState(() => localStorage.getItem('buddee-userType') || 'free');
  const [showMenu, setShowMenu] = useState(false);
  const [expandedName, setExpandedName] = useState(null);
  const [savedStash, setSavedStash] = useState(() => JSON.parse(localStorage.getItem('buddee-stash') || '[]'));
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [showDoseModal, setShowDoseModal] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showOnlyStash, setShowOnlyStash] = useState(false);
  const [view, setView] = useState('browse');
  const [freePool, setFreePool] = useState(null);

  // ── New feature state ─────────────────────────────────────────────────────
  const [moodFilter, setMoodFilter] = useState(null);         // mood label string
  const [mealTypeFilter, setMealTypeFilter] = useState(null); // meal type label
  const [categoryFilter, setCategoryFilter] = useState(null); // category string
  const [servingScales, setServingScales] = useState({});   // { recipeName: 1|2|4 }
  const [strainNotes, setStrainNotes] = useState(          // { recipeName: string }
    () => JSON.parse(localStorage.getItem('buddee-notes') || '{}')
  );
  const [recentlyViewed, setRecentlyViewed] = useState(
    () => JSON.parse(localStorage.getItem('buddee-recentlyViewed') || '[]')
  );
  const [infusionTipFor, setInfusionTipFor] = useState(null); // recipe name
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem('buddee-onboarded')
  );
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile filter drawer

  // ── Auth & session ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleSession = async (session) => {
      const email = session?.user?.email || localStorage.getItem('buddee-userEmail');
      if (!email) return;
      if (session?.user) {
        await fetch('/api/init-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: session.access_token }),
        });
      }
      const isPremium = await checkPremiumStatus(email);
      localStorage.setItem('buddee-userEmail', email);
      localStorage.setItem('buddee-userType', isPremium ? 'premium' : 'free');
      setUserType(isPremium ? 'premium' : 'free');
    };

    const clearPremium = () => {
      localStorage.setItem('buddee-userType', 'free');
      setUserType('free');
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) handleSession(session);
      else clearPremium(); // No active session — reject any cached premium status
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) handleSession(session);
      else clearPremium(); // Signed out — revoke premium
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Load recipes ───────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadRecipes() {
      const local = JSON.parse(localStorage.getItem('buddee-recipes') || '[]');
      try {
        const res = await fetch('/recipes.json');
        const json = await res.json();
        const all = [...json, ...local];
        setRecipes(all);
        const shuffled = [...all].sort(() => Math.random() - 0.5);
        setFreePool(new Set(shuffled.slice(0, 10).map(r => r.recipeName)));
      } catch {
        const all = [...local];
        setRecipes(all);
        const shuffled = [...all].sort(() => Math.random() - 0.5);
        setFreePool(new Set(shuffled.slice(0, 10).map(r => r.recipeName)));
      }
    }
    loadRecipes();
  }, []);

  // ── Persistence ────────────────────────────────────────────────────────────
  useEffect(() => { localStorage.setItem('buddee-userType', userType); }, [userType]);
  useEffect(() => {
    const customRecipes = recipes.filter(r => r.isCustom);
    localStorage.setItem('buddee-recipes', JSON.stringify(customRecipes));
  }, [recipes]);
  useEffect(() => { localStorage.setItem('buddee-stash', JSON.stringify(savedStash)); }, [savedStash]);
  useEffect(() => { localStorage.setItem('buddee-notes', JSON.stringify(strainNotes)); }, [strainNotes]);
  useEffect(() => { localStorage.setItem('buddee-recentlyViewed', JSON.stringify(recentlyViewed)); }, [recentlyViewed]);

  // ── Track recently viewed ──────────────────────────────────────────────────
  useEffect(() => {
    if (!expandedName) return;
    setRecentlyViewed(prev => {
      const without = prev.filter(n => n !== expandedName);
      return [expandedName, ...without].slice(0, 5);
    });
  }, [expandedName]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleRecipeUpdate = (updatedRecipe) => {
    setRecipes(prev => prev.map(r =>
      r.recipeName === updatedRecipe.recipeName ? { ...updatedRecipe, isCustom: true } : r
    ));
    setEditingRecipe(null);
  };

  const handleRestorePurchase = async () => {
    const email = localStorage.getItem('buddee-userEmail');
    if (!email) return showToast('Please sign in first.', 'warning');
    const isPremium = await checkPremiumStatus(email);
    if (isPremium) {
      localStorage.setItem('buddee-userType', 'premium');
      setUserType('premium');
      setShowMenu(false);
    } else {
      showToast('No premium purchase found. If you just purchased, wait a moment and try again.', 'warning');
    }
  };

  const toggleStash = (recipeName) => {
    setSavedStash(prev =>
      prev.includes(recipeName) ? prev.filter(n => n !== recipeName) : [...prev, recipeName]
    );
  };

  const closeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('buddee-onboarded', '1');
  };

  // ── Derived recipe lists ───────────────────────────────────────────────────
  const visibleRecipes = useMemo(() => {
    if (userType === 'premium' || freePool === null) return recipes;
    return recipes.filter(r => freePool.has(r.recipeName));
  }, [recipes, userType, freePool]);

  const allStrains  = [...new Set(visibleRecipes.map(r => r.strain))];
  const allEffects  = [...new Set(visibleRecipes.map(r => r.effect))];
  const allTerpenes = [...new Set(visibleRecipes.map(r => r.terpene))];

  const todaysPick = useMemo(() => getTodaysPick(visibleRecipes), [visibleRecipes]);

  const filteredRecipes = useMemo(() => {
    const activeMood = MOODS.find(m => m.label === moodFilter);
    const activeMealType = MEAL_TYPES.find(m => m.label === mealTypeFilter);

    return visibleRecipes.filter(recipe => {
      const q = query.toLowerCase();
      const matchesSearch = !q ||
        recipe.recipeName.toLowerCase().includes(q) ||
        recipe.effect.toLowerCase().includes(q) ||
        recipe.chefNotes.toLowerCase().includes(q);
      const matchesStrain  = !selectedStrain  || recipe.strain  === selectedStrain;
      const matchesEffect  = !selectedEffect  || recipe.effect  === selectedEffect;
      const matchesTerpene = !selectedTerpene || recipe.terpene === selectedTerpene;
      const matchesStash   = !showOnlyStash   || savedStash.includes(recipe.recipeName);
      const matchesMood    = !activeMood || activeMood.effects.length === 0 ||
        activeMood.effects.includes(recipe.effect);
      const matchesMealType = !activeMealType ||
        activeMealType.tags.some(t => (recipe.tags || []).map(x => x.toLowerCase()).includes(t));
      const matchesCategory = !categoryFilter || recipe.category === categoryFilter;
      return matchesSearch && matchesStrain && matchesEffect && matchesTerpene &&
        matchesStash && matchesMood && matchesMealType && matchesCategory;
    });
  }, [visibleRecipes, query, selectedStrain, selectedEffect, selectedTerpene, showOnlyStash, savedStash, moodFilter, mealTypeFilter, categoryFilter]);

  const TABS = [
    { id: 'browse',  label: 'Browse' },
    { id: 'pantry',  label: 'Pantry Match' },
  ];

  return (
    <div className="min-h-screen font-nunito transition-colors duration-300" style={{ backgroundColor: 'var(--surface-bg)' }}>

      {/* ── Onboarding overlay ── */}
      <AnimatePresence>
        {showOnboarding && <OnboardingOverlay onClose={closeOnboarding} />}
      </AnimatePresence>

      {/* ── Sticky header ── */}
      <header className="sticky top-0 z-40 shadow-sm print:hidden"
        style={{ backgroundColor: 'var(--surface-bg)', borderBottom: '2px solid var(--accent-bg)' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">

          {/* Left — premium quick actions (desktop) */}
          <div className="flex items-center gap-2 flex-1">
            {userType === 'premium' && (
              <>
                <button
                  onClick={() => { setIsCreatingNew(true); setEditingRecipe(null); }}
                  className="hidden sm:block text-sm font-bold px-3 py-1.5 rounded-full border text-white transition hover:opacity-80"
                  style={{ backgroundColor: 'var(--accent-bg)', borderColor: 'var(--accent-bg)' }}
                >
                  ➕ New Recipe
                </button>
                <button
                  onClick={() => setShowDoseModal(true)}
                  className="hidden sm:block text-sm font-bold px-3 py-1.5 rounded-full border transition hover:opacity-80 text-white"
                  style={{ backgroundColor: 'var(--accent-bg)', borderColor: 'var(--accent-bg)' }}
                >
                  Dosing
                </button>
              </>
            )}
          </div>

          {/* Center — brand */}
          <div className="flex flex-col items-center shrink-0">
            <div className="flex items-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL}/chef-buddee.png`}
                alt="Chef Buddee mascot"
                className="h-9 w-auto"
              />
              <h1 className="text-xl sm:text-2xl font-extrabold leading-none"
                style={{ color: 'var(--accent-text)' }}
              >
                Chef Buddee
              </h1>
            </div>
            <p className="text-xs text-sage-900/50 dark:text-zinc-500 italic hidden sm:block">by Puff Puff Peace</p>
          </div>

          {/* Right — account menu */}
          <div className="flex justify-end flex-1">
            <div className="relative">
              <button
                onClick={() => setShowMenu(prev => !prev)}
                className="text-white font-bold px-3 py-1.5 rounded-full text-sm hover:opacity-80 transition"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Profile
              </button>
              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-52 rounded-2xl shadow-xl bg-pp-white dark:bg-zinc-800 border border-sage-900 dark:border-zinc-700 z-50 overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-sage-900/20 dark:border-zinc-700">
                      <ThemePicker />
                    </div>
                    {userType === 'free' && (
                      <>
                        <button
                          onClick={async () => {
                            const res = await fetch('/api/checkout', { method: 'POST' });
                            const data = await res.json();
                            if (data?.url) window.location.href = data.url;
                            else showToast('Failed to start checkout. Please try again.', 'error');
                          }}
                          className="w-full text-left px-4 py-3 font-bold hover:bg-sage-100 dark:hover:bg-zinc-700 transition text-sm border-b border-sage-900/20 dark:border-zinc-700"
                          style={{ color: 'var(--accent-text)' }}
                        >
                          Upgrade to Premium
                        </button>
                        <button
                          onClick={handleRestorePurchase}
                          className="w-full text-left px-4 py-3 text-sage-900/70 dark:text-zinc-300 hover:bg-sage-100 dark:hover:bg-zinc-700 transition text-sm border-b border-sage-900/20 dark:border-zinc-700"
                        >
                          Restore Purchase
                        </button>
                      </>
                    )}
                    <a
                      href="https://www.paypal.com/ncp/payment/5TYWQPR2L9T58"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowMenu(false)}
                      className="block w-full text-left px-4 py-3 text-sage-900/70 dark:text-zinc-300 hover:bg-sage-100 dark:hover:bg-zinc-700 transition text-sm border-t border-sage-900/20 dark:border-zinc-700"
                    >
                      💚 Support Us
                    </a>
                    <button
                      onClick={() => { localStorage.clear(); window.location.href = '/'; }}
                      className="w-full text-left px-4 py-3 text-red-600 dark:text-red-400 font-bold hover:bg-red-50 dark:hover:bg-red-900/20 transition text-sm border-t border-sage-900/20 dark:border-zinc-700"
                    >
                      Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">

        {/* Mobile-only premium buttons */}
        {userType === 'premium' && (
          <div className="flex gap-2 mb-4 sm:hidden">
            <button
              onClick={() => { setIsCreatingNew(true); setEditingRecipe(null); }}
              className="flex-1 text-sm font-bold px-3 py-2 rounded-full text-white"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            >
              ➕ New Recipe
            </button>
            <button
              onClick={() => setShowDoseModal(true)}
              className="flex-1 text-sm font-bold px-3 py-2 rounded-full text-white"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            >
              Dosing Calc
            </button>
          </div>
        )}

        {/* View tabs */}
        <div className="flex gap-2 mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className="px-5 py-2 rounded-full font-bold text-sm transition-all duration-200 border"
              style={
                view === tab.id
                  ? { backgroundColor: 'var(--accent-bg)', color: '#fff', borderColor: 'var(--accent-bg)' }
                  : { backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Pantry Match view ── */}
        {view === 'pantry' && (
          <PantryMatcher
            recipes={visibleRecipes}
            userType={userType}
            totalRecipeCount={recipes.length}
            onViewRecipe={(recipe) => {
              setExpandedName(recipe.recipeName);
              setView('browse');
            }}
          />
        )}

        {/* ── Browse view ── */}
        {view === 'browse' && (
          <>
            {/* Mobile filter toggle */}
            <button
              onClick={() => setFiltersOpen(p => !p)}
              className="md:hidden w-full flex items-center justify-between mb-3 px-4 py-2.5 rounded-xl font-semibold text-sm border border-sage-900/20 dark:border-zinc-700 dark:text-zinc-100"
              style={{ backgroundColor: 'var(--surface-card)', color: 'var(--text-primary)' }}
            >
              <span>🔽 Filters{[moodFilter, mealTypeFilter, categoryFilter, selectedStrain, selectedEffect, selectedTerpene].filter(Boolean).length > 0 && ` (${[moodFilter, mealTypeFilter, categoryFilter, selectedStrain, selectedEffect, selectedTerpene].filter(Boolean).length} active)`}</span>
              <span className="text-sage-900/40 dark:text-zinc-500 text-xs">{filtersOpen ? '▲ Hide' : '▼ Show'}</span>
            </button>

            {/* Filter panel — always visible on md+, collapsible on mobile */}
            <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>

            {/* Mood quick-filters */}
            <div className="mb-3">
              <p className="text-xs font-semibold text-sage-900/50 dark:text-zinc-400 mb-2 uppercase tracking-wide">Mood</p>
              <div className="flex flex-wrap gap-2">
                {MOODS.map(m => (
                  <button
                    key={m.label}
                    onClick={() => setMoodFilter(prev => prev === m.label ? null : m.label)}
                    className="px-4 py-1.5 rounded-full font-semibold text-xs border transition-all"
                    style={
                      moodFilter === m.label
                        ? { backgroundColor: 'var(--accent-bg)', color: '#fff', borderColor: 'var(--accent-bg)' }
                        : { backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Meal type quick-filters */}
            <div className="mb-5">
              <p className="text-xs font-semibold text-sage-900/50 dark:text-zinc-400 mb-2 uppercase tracking-wide">Meal Type</p>
              <div className="flex flex-wrap gap-2">
                {MEAL_TYPES.map(m => (
                  <button
                    key={m.label}
                    onClick={() => setMealTypeFilter(prev => prev === m.label ? null : m.label)}
                    className="px-4 py-1.5 rounded-full font-semibold text-xs border transition-all"
                    style={
                      mealTypeFilter === m.label
                        ? { backgroundColor: 'var(--accent-bg)', color: '#fff', borderColor: 'var(--accent-bg)' }
                        : { backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }
                    }
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Category quick-filters */}
            <div className="mb-5">
              <p className="text-xs font-semibold text-sage-900/50 dark:text-zinc-400 mb-2 uppercase tracking-wide">Category</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(prev => prev === cat ? null : cat)}
                    className="px-4 py-1.5 rounded-full font-semibold text-xs border transition-all"
                    style={
                      categoryFilter === cat
                        ? { backgroundColor: 'var(--accent-bg)', color: '#fff', borderColor: 'var(--accent-bg)' }
                        : { backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }
                    }
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Dropdown filters */}
            <div className="mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="🔍 Search recipes…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="col-span-2 md:col-span-1 w-full p-3 rounded-xl bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 placeholder-zinc-400 border border-sage-900 dark:border-zinc-600 text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': 'var(--accent-bg)' }}
              />
              <select
                value={selectedStrain}
                onChange={e => setSelectedStrain(e.target.value)}
                className="w-full p-3 rounded-xl bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 border border-sage-900 dark:border-zinc-600 text-sm"
              >
                <option value="">All strains</option>
                {allStrains.map((s, i) => (
                  <option key={i} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <select
                value={selectedEffect}
                onChange={e => setSelectedEffect(e.target.value)}
                className="w-full p-3 rounded-xl bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 border border-sage-900 dark:border-zinc-600 text-sm"
              >
                <option value="">All effects</option>
                {allEffects.map((e, i) => <option key={i} value={e}>{e}</option>)}
              </select>
              <select
                value={selectedTerpene}
                onChange={e => setSelectedTerpene(e.target.value)}
                className="w-full p-3 rounded-xl bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 border border-sage-900 dark:border-zinc-600 text-sm"
              >
                <option value="">All terpenes</option>
                {allTerpenes.map((t, i) => <option key={i} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Stash toggle + count row */}
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={() => setShowOnlyStash(prev => !prev)}
                className="px-4 py-2 rounded-full font-semibold text-sm border transition-all duration-200"
                style={
                  showOnlyStash
                    ? { backgroundColor: 'var(--accent-bg)', color: '#fff', borderColor: 'var(--accent-bg)' }
                    : { backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }
                }
              >
                {showOnlyStash ? '❤️ My Stash' : '🤍 My Stash'}
              </button>
              <span className="text-xs text-sage-900/50 dark:text-zinc-400 font-semibold">
                {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? 's' : ''}
              </span>
            </div>

            </div>{/* end collapsible filter panel */}

            {/* Free-tier notice */}
            {userType === 'free' && recipes.length > 10 && (
              <div className="mb-4 flex items-center justify-between gap-3 bg-honeyYellow-100 dark:bg-honeyYellow-900/20 border border-honeyYellow-900/30 dark:border-honeyYellow-900/40 rounded-xl px-4 py-2.5 text-xs text-honeyYellow-900 dark:text-honeyYellow-100">
                <span className="font-semibold">
                  Showing 10 of {recipes.length} recipes — upgrade for the full library.
                </span>
                <button
                  onClick={async () => {
                    const res = await fetch('/api/checkout', { method: 'POST' });
                    const data = await res.json();
                    if (data?.url) window.location.href = data.url;
                    else showToast('Failed to start checkout. Please try again.', 'error');
                  }}
                  className="shrink-0 font-bold underline underline-offset-2 hover:opacity-70 transition"
                >
                  Upgrade
                </button>
              </div>
            )}

            {/* ── Tonight's Pick banner ── */}
            {todaysPick && !moodFilter && !mealTypeFilter && !categoryFilter && !query && !showOnlyStash && (
              <div
                className="mb-5 rounded-2xl p-4 border-2 text-white"
                style={{ backgroundColor: 'var(--accent-bg)', borderColor: 'var(--accent-bg)' }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-0.5">
                      Tonight's Pick
                    </p>
                    <p className="font-extrabold text-lg leading-snug">{todaysPick.recipeName}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs bg-white/15 px-2 py-0.5 rounded-full font-semibold">
                        {todaysPick.effect}
                      </span>
                      <span className="text-xs opacity-70">~{todaysPick.estimatedMgPerServing}mg · {todaysPick.servings} servings</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedName(n => n === todaysPick.recipeName ? null : todaysPick.recipeName)}
                    className="shrink-0 bg-white font-bold text-xs px-4 py-2 rounded-full transition hover:opacity-90"
                    style={{ color: 'var(--accent-bg)' }}
                  >
                    {expandedName === todaysPick.recipeName ? 'Close' : 'View'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Recently Viewed strip ── */}
            {recentlyViewed.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-semibold text-sage-900/50 dark:text-zinc-400 mb-2 uppercase tracking-wide">
                  Recently Viewed
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  {recentlyViewed.map(name => (
                    <button
                      key={name}
                      onClick={() => setExpandedName(prev => prev === name ? null : name)}
                      className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border dark:border-zinc-600 transition hover:opacity-80"
                      style={{ backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Sponsored slot ── */}
            <div className="mb-5 rounded-2xl border border-dashed border-sage-900/20 dark:border-white/10 px-5 py-4 bg-white/25 dark:bg-white/5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-sage-900/30 dark:text-zinc-600 mb-1">Advertising</p>
              <p className="text-sm font-bold text-sage-900/60 dark:text-zinc-300 mb-0.5">Reach cannabis home cooks who are actually cooking.</p>
              <p className="text-xs text-sage-900/40 dark:text-zinc-500 leading-relaxed">Chef Buddee users are ingredient-conscious, brand-loyal, and searching for quality. Bring your products into their kitchen. <span className="font-semibold underline underline-offset-2">Partner with us.</span></p>
            </div>

            {/* Recipe grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredRecipes.map((recipe, index) => {
                const isExpanded  = expandedName === recipe.recipeName;
                const isStashed   = savedStash.includes(recipe.recipeName);
                const scale       = servingScales[recipe.recipeName] || 1;
                const scaledMg    = recipe.estimatedMgPerServing;
                const scaledServings = recipe.servings * scale;

                return (
                  <motion.div
                    key={recipe.recipeName}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(index * 0.04, 0.3) }}
                    className={`bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-sage-900 dark:border-zinc-700 overflow-hidden hover:shadow-md transition-shadow duration-200 ${
                      isExpanded ? 'print:block' : 'print:hidden'
                    }`}
                  >
                    {/* Accent bar */}
                    <div className="h-1" style={{ backgroundColor: 'var(--accent-bg)' }} />

                    <div className="p-4">
                      {/* Title + heart */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <button
                          onClick={() => setExpandedName(isExpanded ? null : recipe.recipeName)}
                          className="text-left font-bold text-sage-900 dark:text-zinc-100 text-base leading-snug hover:underline flex-1"
                        >
                          {recipe.recipeName}
                          <span className="ml-1 text-sage-900/30 dark:text-zinc-600 text-xs">{isExpanded ? '▲' : '▼'}</span>
                        </button>
                        <motion.button
                          whileTap={{ scale: 1.5 }}
                          onClick={() => toggleStash(recipe.recipeName)}
                          className="text-xl leading-none shrink-0 focus:outline-none"
                          title={isStashed ? 'Remove from stash' : 'Save to stash'}
                        >
                          {isStashed ? '❤️' : '🤍'}
                        </motion.button>
                      </div>

                      {/* Intensity dots */}
                      <div className="mb-2">
                        <IntensityDots mg={scaledMg} />
                      </div>

                      {/* Pill badges */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {[recipe.effect, recipe.strain.charAt(0).toUpperCase() + recipe.strain.slice(1), recipe.terpene].map((label, i) => (
                          <span key={i} className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                            style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)', borderColor: 'var(--accent-bg)' }}>
                            {label}
                          </span>
                        ))}
                      </div>

                      <p className="text-xs text-sage-900/50 dark:text-zinc-400 mb-2">
                        ~{scaledMg}mg/serving · {scaledServings} serving{scaledServings !== 1 ? 's' : ''}
                        {scale > 1 && <span className="ml-1 font-semibold text-honeyYellow-900">(scaled ×{scale})</span>}
                      </p>
                      <p className="text-sm italic text-sage-900/70 dark:text-zinc-400 leading-snug">"{recipe.chefNotes}"</p>

                      {/* Tags */}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {(recipe.tags || []).map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)' }}>
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Expanded section */}
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            key="expanded"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 pt-4 border-t border-sage-900/20 dark:border-zinc-700 space-y-3 text-sm text-sage-900 dark:text-zinc-200">

                              {/* Infusion method with tooltip toggle */}
                              <div>
                                <div className="flex items-center gap-1">
                                  <strong>Infusion Method</strong>
                                  <button
                                    onClick={() => setInfusionTipFor(prev =>
                                      prev === recipe.recipeName ? null : recipe.recipeName
                                    )}
                                    className="w-4 h-4 rounded-full bg-sage-900/10 dark:bg-white/10 text-sage-900/60 dark:text-zinc-300 text-xs font-bold leading-none flex items-center justify-center hover:bg-sage-900/20 dark:hover:bg-white/20 transition shrink-0"
                                    title="What does this mean?"
                                  >
                                    ?
                                  </button>
                                </div>
                                <AnimatePresence>
                                  {infusionTipFor === recipe.recipeName && (
                                    <motion.p
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      className="mt-1 text-xs dark:text-zinc-300 rounded-lg px-3 py-2 leading-relaxed overflow-hidden"
                            style={{ backgroundColor: 'var(--accent-light)', color: 'var(--text-primary)' }}
                                    >
                                      {recipe.infusionMethod}
                                    </motion.p>
                                  )}
                                </AnimatePresence>
                              </div>

                              {/* Serving scaler */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-sage-900/60 dark:text-zinc-400">Scale recipe:</span>
                                {[1, 2, 4].map(s => (
                                  <button
                                    key={s}
                                    onClick={() => setServingScales(prev => ({ ...prev, [recipe.recipeName]: s }))}
                                    className="text-xs font-bold px-2.5 py-1 rounded-full border transition"
                                    style={
                                      scale === s
                                        ? { backgroundColor: 'var(--accent-bg)', color: '#fff', borderColor: 'var(--accent-bg)' }
                                        : { backgroundColor: 'var(--surface-card)', color: 'var(--accent-text)', borderColor: 'var(--accent-text)' }
                                    }
                                  >
                                    ×{s}
                                  </button>
                                ))}
                              </div>

                              {recipe.ingredients?.length > 0 && (
                                <div>
                                  <p className="font-semibold mb-1">
                                    Ingredients
                                    {scale > 1 && <span className="text-xs font-normal text-honeyYellow-900 ml-1">(×{scale})</span>}
                                  </p>
                                  <ul className="list-disc list-inside text-sage-900/80 dark:text-zinc-300 space-y-0.5">
                                    {recipe.ingredients.map((item, idx) => (
                                      <li key={idx}>{scaleIngredient(item, scale)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              <div>
                                <p className="font-semibold mb-1">Instructions</p>
                                <ol className="list-decimal list-inside text-sage-900/80 dark:text-zinc-300 space-y-1">
                                  {recipe.instructions.map((step, i) => (
                                    <li key={i}>{step}</li>
                                  ))}
                                </ol>
                              </div>

                              {/* Action buttons */}
                              <div className="flex gap-2 flex-wrap pt-1">
                                <button
                                  onClick={() => {
                                    if (userType !== 'premium') return showToast('Premium required to print recipes.', 'warning');
                                    setTimeout(() => window.print(), 100);
                                  }}
                                  className="px-3 py-1.5 text-white font-bold text-xs rounded-full print:hidden transition hover:opacity-80"
                                  style={{ backgroundColor: 'var(--accent-bg)' }}
                                >
                                  Print
                                </button>
                                <button
                                  onClick={() => {
                                    const url = `${window.location.origin}?recipe=${encodeURIComponent(recipe.recipeName)}`;
                                    navigator.clipboard.writeText(url)
                                      .then(() => showToast('Link copied to clipboard!', 'success'))
                                      .catch(() => showToast('Failed to copy link.', 'error'));
                                  }}
                                  className="px-3 py-1.5 border font-bold text-xs rounded-full"
                                  style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)', borderColor: 'var(--accent-bg)' }}
                                >
                                  Share
                                </button>
                                {userType === 'premium' && (
                                  <button
                                    onClick={() => setEditingRecipe(recipe)}
                                    className="px-3 py-1.5 border font-bold text-xs rounded-full"
                                    style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)', borderColor: 'var(--accent-bg)' }}
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>

                              {/* Strain notes — premium only */}
                              {userType === 'premium' && (
                                <div>
                                  <p className="font-semibold text-xs text-sage-900/60 dark:text-zinc-400 mb-1 uppercase tracking-wide">
                                    My Notes
                                  </p>
                                  <textarea
                                    value={strainNotes[recipe.recipeName] || ''}
                                    onChange={e => setStrainNotes(prev => ({
                                      ...prev,
                                      [recipe.recipeName]: e.target.value,
                                    }))}
                                    placeholder="Jot down strain tips, tweaks, or tasting notes…"
                                    rows={2}
                                    className="w-full text-xs p-2 rounded-lg border border-sage-900/30 dark:border-zinc-600 bg-sage-100 dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 placeholder-sage-900/30 dark:placeholder-zinc-500 resize-none focus:outline-none focus:ring-1"
                                    style={{ '--tw-ring-color': 'var(--accent-bg)' }}
                                  />
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}

              {/* Premium upsell card */}
              {userType === 'free' && (
                <div
                  className="text-white rounded-2xl shadow-sm p-6 flex flex-col items-center justify-center text-center gap-3"
                  style={{ backgroundColor: 'var(--accent-bg)' }}
                >
                  <h3 className="font-bold text-lg">Go Premium</h3>
                  <p className="text-sm text-white/70">Custom recipes, dosing calculator, print &amp; more.</p>
                  <span className="text-xs font-bold bg-white/10 px-3 py-1 rounded-full">One-time $25</span>
                  <button
                    onClick={async () => {
                      const res = await fetch('/api/checkout', { method: 'POST' });
                      const data = await res.json();
                      if (data?.url) window.location.href = data.url;
                      else showToast('Failed to start checkout. Please try again.', 'error');
                    }}
                    className="mt-1 bg-white font-bold px-5 py-2 rounded-full text-sm transition hover:opacity-90"
                    style={{ color: 'var(--accent-bg)' }}
                  >
                    Upgrade Now
                  </button>
                </div>
              )}
            </div>

            {/* ── Sponsored slot (bottom) ── */}
            {filteredRecipes.length > 0 && (
              <div className="mt-6 rounded-2xl border border-dashed border-sage-900/20 dark:border-white/10 px-5 py-4 bg-white/25 dark:bg-white/5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-sage-900/30 dark:text-zinc-600 mb-1">Advertising</p>
                <p className="text-sm font-bold text-sage-900/60 dark:text-zinc-300 mb-0.5">A direct line to the cannabis kitchen.</p>
                <p className="text-xs text-sage-900/40 dark:text-zinc-500 leading-relaxed">From specialty equipment to premium ingredients — if it belongs in an infused kitchen, our audience is actively looking for it. <span className="font-semibold underline underline-offset-2">Let's build something together.</span></p>
              </div>
            )}

            {/* Empty state */}
            {filteredRecipes.length === 0 && (
              <div className="text-center py-16">
                <p className="text-sage-900 dark:text-zinc-100 font-bold text-lg">Nothing found, Chef.</p>
                <p className="text-sm text-sage-900/60 dark:text-zinc-400 mt-1">Try adjusting your filters or search.</p>
                {(moodFilter || mealTypeFilter || categoryFilter) && (
                  <button
                    onClick={() => { setMoodFilter(null); setMealTypeFilter(null); setCategoryFilter(null); }}
                    className="mt-3 text-xs font-bold underline underline-offset-2"
                    style={{ color: 'var(--accent-text)' }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Modals */}
      {editingRecipe && (
        <RecipeEditorModal
          initialData={editingRecipe}
          onSave={handleRecipeUpdate}
          onClose={() => setEditingRecipe(null)}
        />
      )}
      {isCreatingNew && (
        <RecipeEditorModal
          initialData={null}
          onSave={(newRecipe) => {
            setRecipes(prev => [...prev, { ...newRecipe, isCustom: true }]);
            setIsCreatingNew(false);
          }}
          onClose={() => setIsCreatingNew(false)}
        />
      )}
      {showDoseModal && (
        <DosingCalculatorModal onClose={() => setShowDoseModal(false)} />
      )}

    </div>
  );
}
